import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * The canonical tab strip: underline tabs with optional count badges sitting
 * on a divider line (the style from the affiliate-program detail card).
 *
 * `accent="terracotta"` keeps the admin console on its own accent instead of
 * the tenant brand color.
 */

export interface UnderlineTabItem<T extends string = string> {
  value: T;
  label: React.ReactNode;
  /** Count badge; omit (or null) to render no badge. */
  count?: number | null;
  /** Rendered muted; clicks go to onDisabledClick instead of onValueChange. */
  disabled?: boolean;
  id?: string;
}

export interface UnderlineTabsProps<T extends string = string> {
  tabs: UnderlineTabItem<T>[];
  value: T;
  onValueChange: (value: T) => void;
  /** Called when a disabled tab is clicked (e.g. to explain why via toast). */
  onDisabledClick?: (value: T) => void;
  accent?: "brand" | "terracotta";
  /** Content rendered at the right end of the strip (e.g. a search box). */
  trailing?: React.ReactNode;
  className?: string;
}

const ACCENTS = {
  brand: {
    active: "border-brand-600 text-brand-700 dark:text-brand-400",
    badgeActive: "bg-brand-100 text-brand-700 dark:text-brand-300",
  },
  terracotta: {
    active: "border-[#d7785b] text-[#d7785b]",
    badgeActive:
      "bg-orange-100 text-[#d7785b] dark:bg-orange-500/15 dark:text-orange-300",
  },
} as const;

export function UnderlineTabs<T extends string = string>({
  tabs,
  value,
  onValueChange,
  onDisabledClick,
  accent = "brand",
  trailing,
  className,
}: UnderlineTabsProps<T>) {
  const colors = ACCENTS[accent];

  return (
    <div
      className={cn(
        // flex-wrap: on narrow screens a fixed-width `trailing` (e.g. a
        // search box) drops below the strip instead of squeezing the tabs
        // into a cut-off sliver.
        "flex flex-wrap items-end justify-between gap-x-6 gap-y-2 border-b border-border",
        className,
      )}
    >
      {/* -mb-px sits on the scroll container, not the buttons: an overhanging
          button inside overflow-x-auto creates a 1px vertical scrollbar. */}
      <div
        role="tablist"
        className="-mb-px flex max-w-full gap-1 min-w-0 overflow-x-auto scrollbar-hide"
      >
        {tabs.map((tab) => {
          const isActive = tab.value === value;
          return (
            <button
              key={tab.value}
              id={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-disabled={tab.disabled || undefined}
              onClick={() =>
                tab.disabled
                  ? onDisabledClick?.(tab.value)
                  : onValueChange(tab.value)
              }
              className={cn(
                "flex shrink-0 items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium transition-colors",
                tab.disabled
                  ? "border-transparent text-muted-foreground/50 cursor-not-allowed"
                  : isActive
                    ? colors.active
                    : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {tab.label}
              {tab.count != null && (
                <span
                  className={cn(
                    "rounded-full px-1.5 py-0.5 text-[11px] font-semibold",
                    isActive
                      ? colors.badgeActive
                      : "bg-muted text-muted-foreground",
                  )}
                >
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {trailing ? <div className="min-w-0 shrink-0 pb-1.5">{trailing}</div> : null}
    </div>
  );
}
